import { useCompany } from "api/company.api";
import { PageLoader } from "components/PageLoader";
import { capitalizeFirstLetter } from "helpers/string";
import { useEffect, useMemo } from "react";
import userStore, { getUserCompanyId } from "store/user.store";
import { Route } from "routes/_authenticated/app.organisation";
import { ApiTab } from "./ApiTab";
import { NaturvardsverketTab } from "./NaturvardsverketTab/NaturvardsverketTab";
import { OperatorRightsTab } from "./OperatorRightsTab";
import { OverviewTab } from "./OverviewTab";
import { UsersTab } from "./UsersTab";
import { VehicleTab } from "./VehicleTab";
import { OrgTabBar, type OrgTabItem } from "./OrgTabBar";

import { ErrorScreen } from "components/ErrorScreen";
import {
  BuildingOffice2Icon,
  UsersIcon,
  TruckIcon,
  GlobeEuropeAfricaIcon,
  KeyIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

export enum OrganizationPageTabs {
  Allmänt = "uppgifter",
  Users = "användare",
  Vehicles = "fordon",
  Naturvardsverket = "naturvårdsverket",
  Api = "api",
  Rättigheter = "rättigheter",
}
export const MyCompanyPage = (): JSX.Element => {
  const { tab } = Route.useSearch();
  const currentTab: OrganizationPageTabs = tab || OrganizationPageTabs.Allmänt;
  const companyId = userStore(getUserCompanyId) ?? -1; // kommer aldrig hända

  useEffect(() => {
    document.title = "Organisation";
  }, []);

  // Get company
  const { data: company, isLoading: isLoadingCompany } = useCompany(companyId);

  // The API tab only exists for Norva companies. Resolve the requested tab so
  // ?tab=api on a non-Norva company falls back to the default tab instead of
  // leaving the page with no active tab and no rendered content.
  const isApiTabAvailable = !!company?.isNorva;
  const activeTab: OrganizationPageTabs =
    currentTab === OrganizationPageTabs.Api && !isApiTabAvailable
      ? OrganizationPageTabs.Allmänt
      : currentTab;

  const tabMenuItems = useMemo<OrgTabItem[]>(
    () =>
      [
        {
          label: capitalizeFirstLetter(OrganizationPageTabs.Allmänt),
          value: OrganizationPageTabs.Allmänt,
          icon: BuildingOffice2Icon,
          isActive: activeTab === OrganizationPageTabs.Allmänt,
          link: {
            search: { tab: OrganizationPageTabs.Allmänt },
          },
        },
        {
          label: capitalizeFirstLetter(OrganizationPageTabs.Users),
          value: OrganizationPageTabs.Users,
          icon: UsersIcon,
          isActive: activeTab === OrganizationPageTabs.Users,
          link: {
            search: { tab: OrganizationPageTabs.Users },
          },
        },
        {
          label: capitalizeFirstLetter(OrganizationPageTabs.Vehicles),
          value: OrganizationPageTabs.Vehicles,
          icon: TruckIcon,
          isActive: activeTab === OrganizationPageTabs.Vehicles,
          link: {
            search: { tab: OrganizationPageTabs.Vehicles },
          },
        },
        {
          label: capitalizeFirstLetter(OrganizationPageTabs.Naturvardsverket),
          value: OrganizationPageTabs.Naturvardsverket,
          icon: GlobeEuropeAfricaIcon,
          isActive: activeTab === OrganizationPageTabs.Naturvardsverket,
          link: {
            search: { tab: OrganizationPageTabs.Naturvardsverket },
          },
        },
        {
          label: "API",
          value: OrganizationPageTabs.Api,
          icon: KeyIcon,
          isActive: activeTab === OrganizationPageTabs.Api,
          link: {
            search: { tab: OrganizationPageTabs.Api },
          },
        },
        {
          label: capitalizeFirstLetter(OrganizationPageTabs.Rättigheter),
          value: OrganizationPageTabs.Rättigheter,
          icon: ShieldCheckIcon,
          isActive: activeTab === OrganizationPageTabs.Rättigheter,
          link: {
            search: { tab: OrganizationPageTabs.Rättigheter },
          },
        },
      ].filter((x) => x.value !== OrganizationPageTabs.Api || isApiTabAvailable),
    [activeTab, isApiTabAvailable],
  );

  if (isLoadingCompany) {
    return <PageLoader />;
  }

  if (!company) {
    return <ErrorScreen />;
  }

  return (
    <div className="mx-auto max-w-5xl pb-12">
      <header className="mb-6 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">
          Inställningar
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">
          Organisation
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Hantera {company.name} – uppgifter, användare, fordon och
          rapportering.
        </p>
      </header>

      <OrgTabBar items={tabMenuItems} className="mb-6" />

      {activeTab === OrganizationPageTabs.Allmänt && (
        <OverviewTab company={company} />
      )}
      {activeTab === OrganizationPageTabs.Users && <UsersTab />}
      {activeTab === OrganizationPageTabs.Vehicles && <VehicleTab />}
      {activeTab === OrganizationPageTabs.Naturvardsverket && (
        <NaturvardsverketTab company={company} />
      )}
      {activeTab === OrganizationPageTabs.Api && company.isNorva && (
        <ApiTab company={company} />
      )}
      {activeTab === OrganizationPageTabs.Rättigheter && (
        <OperatorRightsTab company={company} />
      )}
    </div>
  );
};
